import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState } from "react";
export default function EndpointTable({ data }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "running":
        return "bg-green-100 text-green-800";
      case "stopped":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPercentageColor = (percentage) => {
    const value = parseFloat(percentage);
    if (value >= 90) return "text-green-600";
    if (value >= 75) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <div className="mt-12 px-4 w-full mx-auto">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200">
        <div className="p-6">
          <h3 className="text-2xl font-bold text-[#003366] mb-6 text-center">
            Endpoints Overview
          </h3>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#003366]/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Endpoint Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Success Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Failures
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Latest Run (UTC)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(data) &&
                  data
                    ?.slice(
                      (currentPage - 1) * itemsPerPage,
                      currentPage * itemsPerPage
                    )
                    .map((row, i) => (
                      <tr key={i} className="hover:bg-[#003366]/5">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={`/endpoint/${row.endpoint}`}
                            className="text-[#003366] hover:text-[#004488] font-medium"
                          >
                            {row.endpoint}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`font-medium ${getPercentageColor(
                              row.availabilityPercentage
                            )}`}
                          >
                            {parseFloat(row.availabilityPercentage).toFixed(2)}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                          {row.downRequests}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                          {new Date(row.latestTimestamp).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                              row.latestStatus
                            )}`}
                          >
                            {row.latestStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>

            {(!Array.isArray(data) || data.length === 0) &&
              (data.length === 0 ? (
                <>
                  <div className="flex  justify-center items-center">
                    <div className="w-12 h-12 m-10 border-4 border-blue-200 border-t-[#003366] rounded-full animate-spin"></div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <div className="text-[#003366]">
                    <svg
                      className="w-12 h-12 mx-auto mb-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 12H4"
                      />
                    </svg>
                    <p className="text-lg font-semibold">No Data Available</p>
                    <p className="mt-2">
                      There is currently no endpoint data to display
                    </p>
                  </div>
                </div>
              ))}
          </div>
          {data?.length > 0 && (
            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center text-sm text-gray-500">
                <span>
                  Showing{" "}
                  {Math.min(data.length, (currentPage - 1) * itemsPerPage + 1)}{" "}
                  to {Math.min(data.length, currentPage * itemsPerPage)} of{" "}
                  {data.length} results
                </span>
                <div className="ml-4">
                  <select
                    className="border border-gray-300 rounded p-1 text-sm"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={5}>5 per page</option>
                    <option value={15}>15 per page</option>
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-1">
                {/* First page button */}
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded ${
                    currentPage === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-200"
                  }`}
                  aria-label="First page"
                >
                  <span className="flex">
                    <ChevronLeft className="h-4 w-4" />
                    <ChevronLeft className="h-4 w-4 -ml-2" />
                  </span>
                </button>

                {/* Previous page button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded ${
                    currentPage === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-200"
                  }`}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {/* Page Numbers */}
                <div className="flex items-center mx-2">
                  {Array.from(
                    {
                      length: Math.min(
                        5,
                        Math.ceil(data.length / itemsPerPage)
                      ),
                    },
                    (_, i) => {
                      const totalPages = Math.ceil(data.length / itemsPerPage);
                      let pageNum;

                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 flex items-center justify-center rounded ${
                            currentPage === pageNum
                              ? "bg-[#003366] text-white"
                              : "text-gray-700 hover:bg-gray-200"
                          }`}
                          aria-label={`Page ${pageNum}`}
                          aria-current={
                            currentPage === pageNum ? "page" : undefined
                          }
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={
                    currentPage === Math.ceil(data.length / itemsPerPage)
                  }
                  className={`p-2 rounded ${
                    currentPage === Math.ceil(data.length / itemsPerPage)
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-200"
                  }`}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() =>
                    handlePageChange(Math.ceil(data.length / itemsPerPage))
                  }
                  disabled={
                    currentPage === Math.ceil(data.length / itemsPerPage)
                  }
                  className={`p-2 rounded ${
                    currentPage === Math.ceil(data.length / itemsPerPage)
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-200"
                  }`}
                  aria-label="Last page"
                >
                  <span className="flex">
                    <ChevronRight className="h-4 w-4" />
                    <ChevronRight className="h-4 w-4 -ml-2" />
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
